import { ArrowRight } from "lucide-react";
import { Reveal, MaskedLines } from "@/components/Reveal";
import { IMAGES } from "@/lib/site";

const BLOCK_TYPES = ["Narrow", "Sloping", "Corner", "Triangular", "Battle-Axe", "Irregular"];

const SiteDiagram = () => (
  <svg viewBox="0 0 480 360" className="w-full h-auto" role="img" aria-label="Site study diagram of a home designed for an irregular-shaped block">
    <rect width="480" height="360" fill="none" />
    {/* boundary — irregular block */}
    <path d="M70 60 L330 40 L420 130 L395 300 L150 320 L60 220 Z" fill="rgba(84,186,250,0.05)" stroke="#5AA6E8" strokeWidth="1.5" strokeDasharray="7 6" />
    {/* house footprint */}
    <path d="M150 130 L290 110 L330 190 L290 260 L165 245 Z" fill="rgba(84,186,250,0.16)" stroke="#54BAFA" strokeWidth="2" />
    <path d="M190 155 L250 148 L250 205 L190 210 Z" fill="none" stroke="#A8D4FF" strokeWidth="1.2" />
    {/* setback lines */}
    <path d="M82 72 L318 54" stroke="#5A6065" strokeWidth="1" strokeDasharray="3 5" />
    <path d="M406 140 L384 288" stroke="#5A6065" strokeWidth="1" strokeDasharray="3 5" />
    {/* entry path */}
    <path d="M290 260 C300 285 285 300 240 308" stroke="#9DBBAA" strokeWidth="1.4" fill="none" strokeDasharray="2 4" />
    {/* street */}
    <path d="M20 335 L460 322" stroke="#FAF7F2" strokeWidth="2" opacity="0.5" />
    <text x="24" y="352" fill="rgba(250,247,242,0.55)" fontSize="11" fontFamily="Space Mono, monospace" letterSpacing="3">STREET</text>
    {/* labels */}
    <text x="176" y="192" fill="#FBF9F5" fontSize="12" fontFamily="Space Mono, monospace" letterSpacing="2">HOME</text>
    <text x="86" y="100" fill="rgba(250,247,242,0.65)" fontSize="10" fontFamily="Space Mono, monospace" letterSpacing="2">SETBACK</text>
    <text x="330" y="80" fill="rgba(250,247,242,0.65)" fontSize="10" fontFamily="Space Mono, monospace" letterSpacing="2">N ↑</text>
  </svg>
);

export default function IrregularBlocks() {
  return (
    <section id="irregular-blocks" className="relative bg-ink text-cream grain py-24 sm:py-32 overflow-hidden" data-testid="irregular-blocks-section">
      <div className="max-w-[1440px] mx-auto px-5 sm:px-8 lg:px-12 grid lg:grid-cols-2 gap-14 lg:gap-20 items-center relative z-10">
        <div className="order-2 lg:order-1">
          <Reveal>
            <p className="eyebrow text-sky mb-6">Our Speciality</p>
          </Reveal>
          <MaskedLines
            className="font-serif font-medium tracking-tight leading-[1.05] text-4xl sm:text-5xl lg:text-6xl"
            lines={[<>An Unusual Block</>, <>Doesn't Mean an</>, <><span className="italic text-gradient">Impossible Dream.</span></>]}
          />
          <Reveal delay={0.2}>
            <p className="mt-8 text-base sm:text-lg leading-relaxed text-cream/75">
              Own an irregular, narrow, sloping or uniquely shaped block? ArcView Homes specialises
              in creating custom home solutions designed to make the most of challenging land.
            </p>
            <p className="mt-5 text-base sm:text-lg leading-relaxed text-cream/75">
              Rather than forcing a standard plan onto a site that isn't standard, our team works
              with the characteristics of your block — its shape, orientation, slope and outlook —
              to develop a home design tailored to the land and to the way you want to live.
            </p>
            <div className="mt-8 border-l-2 border-arcblue pl-5 sm:pl-6">
              <p className="font-serif italic text-lg sm:text-xl leading-relaxed text-cream/90">
                Behind every design is a very talented in-house architect with years of experience
                — someone who can turn an irregular lot into a masterpiece through deep knowledge,
                experience, thoughtful designs and unique thinking, shaping plans that fall in
                line with the requirements of all the relevant approval departments.
              </p>
            </div>
            <div className="flex flex-wrap gap-2.5 mt-8" data-testid="irregular-block-type-chips">
              {BLOCK_TYPES.map((t) => (
                <span key={t} className="font-mono text-[10px] tracking-[0.2em] uppercase border border-cream/25 rounded-full px-4 py-2 text-cream/70">
                  {t}
                </span>
              ))}
            </div>
            <div className="mt-10 flex flex-col sm:flex-row sm:items-center gap-5">
              <a
                href="#contact"
                data-testid="irregular-cta-discuss-block"
                className="group inline-flex items-center justify-center gap-3 bg-arc text-cream font-semibold text-[15px] px-8 py-4 rounded-full hover:bg-arc-dark hover:shadow-xl hover:shadow-black/30 transition-all duration-300"
              >
                Discuss Your Block With Us
                <ArrowRight size={17} className="group-hover:translate-x-1 transition-transform duration-300" />
              </a>
              <p className="font-mono text-[11px] tracking-[0.14em] uppercase text-cream/55">
                Bring us your block. We'll help you explore what's possible.
              </p>
            </div>
          </Reveal>
        </div>

        <div className="order-1 lg:order-2 relative">
          <Reveal delay={0.1}>
            <div className="relative bg-graphite border border-cream/10 p-6 sm:p-8" data-testid="irregular-site-diagram">
              <p className="font-mono text-[10px] tracking-[0.3em] uppercase text-cream/50 mb-4">
                Site Study — Irregular Block
              </p>
              <SiteDiagram />
            </div>
            <div className="relative -mt-10 ml-auto w-[62%] border-4 border-ink overflow-hidden shadow-2xl shadow-black/40">
              <img
                src={IMAGES.irregular}
                alt="A contemporary home designed for a sloping site"
                loading="lazy"
                className="w-full h-52 sm:h-64 object-cover"
                data-testid="irregular-image"
              />
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
}
